import React, { useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';

import { styled } from "@mui/system";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { CgOrganisation } from "react-icons/cg";
import axios from "axios";

const StyledCard = styled(Card)(({ theme }) => ({
  background: "#1A1A1A",
  color: "#fff",
  textAlign: "left",
  padding: "16px",
  minHeight: "380px",
  maxWidth: "372px",
  borderRadius: "30px",
  position: "relative",
  boxShadow: "0px 4px 10px rgba(0,0,0,0.5)",

  [theme.breakpoints.up("md")]: {
    padding: "40px",
  },
}));

const HighlightedCard = styled(StyledCard)(({ theme }) => ({
  background:
    "linear-gradient(262.41deg, #1DA1F2 -8.32%, #6843EC 93.3%)",
}));

const FeatureItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "10px",
});

const PlanCards = ({
  productName,
  title,
  plans,
}) => {
  const [openPlanFormmodel, setOpenPlanFormmodel] =
    useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [selectedPlan, setSelectedPlan] =
    useState({
      plan: "",
      product: "",
    });

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
  });

  const handleOpenPlanFormmodel = (
    planTitle
  ) => {
    setSelectedPlan({
      plan: planTitle,
      product: productName,
    });

    setOpenPlanFormmodel(true);
  };

  const handleClosePlanFormmodel =
    () => {
      setOpenPlanFormmodel(false);
    };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        name: formData?.fullName,
        email: formData?.email,
        phone: formData?.phone,
        productName: selectedPlan?.product,
        planName: selectedPlan?.plan,
        orgName: formData?.orgName,
      };

      const response = await axios.post(
        "https://dev.ienergydigital.com/task-api/api/website-plan-inquiries",
        payload
      );

      if (
        response?.status === 200 ||
        response?.status === 201
      ) {
        setSnackbar({
          open: true,
          message:
            "Your request has been submitted successfully.",
          severity: "success",
        });

        // RESET FORM

        setFormData({
          fullName: "",
          email: "",
          phone: "",
          orgName: ""
        });

        setTimeout(() => {
          handleClosePlanFormmodel();
        }, 2000);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Something went wrong. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <Box
      sx={{
        padding: "60px 24px",
        textAlign: "center",
        background: "#000",
        color: "#fff",
        position: "relative",

        "&::after": {
          content: '""',
          position: "absolute",
          width: "90%",
          height: "50%",
          right: "65%",
          top: "30%",
          bottom: "10%",
          background:
            "radial-gradient(circle, rgba(99, 102, 241, 0.65) 0%, rgba(99, 102, 241, 0) 70%)",
          filter: "blur(100px)",
          zIndex: 0,
        },
      }}
    >
      <Box
        sx={{
          zIndex: 2,
          position: "relative",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            marginBottom: {
              xs: 5,
              md: 10,
            },
            fontWeight: "bold",
            color: "#6B8AFE",
          }}
        >
          {title}
        </Typography>

        <Grid
          container
          spacing={2}
          justifyContent="center"
        >
          {plans?.map(
            (plan, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={index}
              >
                {plan?.popular ? (
                  <HighlightedCard
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection:
                        "column",
                    }}
                  >
                    <Chip
                      label="Most Popular"
                      color="primary"
                      sx={{
                        position:
                          "absolute",
                        top: {
                          xs: 14,
                          md: 20,
                        },
                        right: 28,
                        fontSize: "12px",
                        border:
                          "0.5px solid white",
                      }}
                    />

                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection:
                          "column",
                        flex: 1,
                        height: "100%",
                      }}
                    >
                      <Typography
                        sx={{
                          marginBottom: 5,
                          fontWeight: 400,
                          fontSize: "26px",
                        }}
                      >
                        {plan.title}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          marginBottom: 4,
                        }}
                      >
                        {plan.subtitle}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 700,
                          marginBottom: 4,
                        }}
                      >
                        What's included?
                      </Typography>

                      <Box
                        sx={{ flex: 1 }}
                      >
                        {plan?.features?.map(
                          (
                            feature,
                            i
                          ) => (
                            <FeatureItem
                              key={i}
                            >
                              <CheckCircleIcon />

                              <Typography variant="body2">
                                {
                                  feature
                                }
                              </Typography>
                            </FeatureItem>
                          )
                        )}
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() =>
                          handleOpenPlanFormmodel(
                            plan.title
                          )
                        }
                        sx={{
                          marginTop: 2,
                          color: "#fff",
                          backgroundColor:
                            "#07329F",
                          borderRadius:
                            "10px",
                          height: 42,
                          textTransform:
                            "none",
                          fontWeight: 700,
                        }}
                      >
                        Get Started
                      </Button>
                    </CardContent>
                  </HighlightedCard>
                ) : (
                  <StyledCard
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection:
                        "column",
                    }}
                  >
                    {plan.recommended && (
                      <Chip
                        label="Recommended"
                        color="primary"
                        sx={{
                          position:
                            "absolute",
                          top: {
                            xs: 14,
                            md: 20,
                          },
                          right: 28,
                          fontSize:
                            "12px",
                          borderRadius:
                            "20px",
                          border:
                            "1px solid var(--stroke1, #98F9FF)",
                          background:
                            "transparent",
                        }}
                      />
                    )}

                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection:
                          "column",
                        flex: 1,
                        height: "100%",

                      }}
                    >
                      <Typography
                        sx={{
                          marginBottom: 5,
                          marginTop: 2,
                          fontWeight: 400,
                          fontSize: "26px",
                        }}
                      >
                        {plan.title}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          marginBottom: 4,
                        }}
                      >
                        {plan.subtitle}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 700,
                          marginBottom: 4,
                        }}
                      >
                        What's included?
                      </Typography>

                      <Box
                        sx={{ flex: 1 }}
                      >
                        {plan?.features?.map(
                          (
                            feature,
                            i
                          ) => (
                            <FeatureItem
                              key={i}
                            >
                              <CheckCircleIcon />

                              <Typography variant="body2">
                                {
                                  feature
                                }
                              </Typography>
                            </FeatureItem>
                          )
                        )}
                      </Box>

                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() =>
                          handleOpenPlanFormmodel(
                            plan.title
                          )
                        }
                        sx={{
                          marginTop: 2,
                          color: "#fff",
                          borderColor:
                            "#fff",
                          height: 42,
                          borderRadius:
                            "10px",
                          textTransform:
                            "none",
                          fontWeight: 700,
                        }}
                      >
                        Get Started
                      </Button>
                    </CardContent>
                  </StyledCard>
                )}
              </Grid>
            )
          )}
        </Grid>
      </Box>

      {/* MODAL */}

      <Dialog
        open={openPlanFormmodel}
        onClose={
          handleClosePlanFormmodel
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            background: "#111827",
            color: "#fff",
            overflow: "hidden",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 4,
            position: "relative",
          }}
        >
          <IconButton
            onClick={
              handleClosePlanFormmodel
            }
            sx={{
              position: "absolute",
              right: 14,
              top: 14,
              color: "#fff",
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            sx={{
              fontSize: "30px",
              fontWeight: 700,
              mb: 1,
            }}
          >
            Get Started
          </Typography>

          <Typography
            sx={{
              color: "#9CA3AF",
              mb: 4,
              fontSize: "15px",
            }}
          >
            {
              selectedPlan.product
            }{" "}
            —{" "}
            {
              selectedPlan.plan
            }
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            <TextField
              fullWidth
              placeholder="Full Name"
              name="fullName"
              value={
                formData.fullName
              }
              onChange={
                handleChange
              }
              InputProps={{
                startAdornment:
                  (
                    <InputAdornment position="start">
                      <PersonIcon
                        sx={{
                          color:
                            "#6366F1",
                        }}
                      />
                    </InputAdornment>
                  ),
              }}
              sx={{
                "& .MuiOutlinedInput-root":
                {
                  borderRadius:
                    "12px",
                  color: "#fff",
                },
              }}
            />

            <TextField
              fullWidth
              placeholder="Email Address"
              name="email"
              value={
                formData.email
              }
              onChange={
                handleChange
              }
              InputProps={{
                startAdornment:
                  (
                    <InputAdornment position="start">
                      <EmailIcon
                        sx={{
                          color:
                            "#6366F1",
                        }}
                      />
                    </InputAdornment>
                  ),
              }}
              sx={{
                "& .MuiOutlinedInput-root":
                {
                  borderRadius:
                    "12px",
                  color: "#fff",
                },
              }}
            />

            <TextField
              fullWidth
              placeholder="Phone Number"
              name="phone"
              value={
                formData.phone
              }
              onChange={
                handleChange
              }
              InputProps={{
                startAdornment:
                  (
                    <InputAdornment position="start">
                      <PhoneIcon
                        sx={{
                          color:
                            "#6366F1",
                        }}
                      />
                    </InputAdornment>
                  ),
              }}
              sx={{
                "& .MuiOutlinedInput-root":
                {
                  borderRadius:
                    "12px",
                  color: "#fff",
                },
              }}
            />

            <TextField
              fullWidth
              placeholder="Organization Name"
              name="orgName"
              value={
                formData?.orgName
              }
              onChange={
                handleChange
              }
              InputProps={{
                startAdornment:
                  (
                    <InputAdornment position="start">
                      <CorporateFareOutlinedIcon
                        sx={{
                          color:
                            "#6366F1",
                        }}
                      />
                    </InputAdornment>
                  ),
              }}
              sx={{
                "& .MuiOutlinedInput-root":
                {
                  borderRadius:
                    "12px",
                  color: "#fff",
                },
              }}
            />



            <Button
              variant="contained"
              fullWidth
              onClick={
                handleSubmit
              }
              sx={{
                mt: 1,
                height: 50,
                borderRadius:
                  "12px",
                background:
                  "linear-gradient(135deg, #19aeee 0%, #7045ba 100%)",
                fontWeight: 700,
                textTransform:
                  "none",
              }}
            >
              Submit
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar?.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          severity={snackbar?.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "12px",
            fontWeight: 600,
          }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanCards;